// Only the media CDN distributions resize on `?width=`, and they resize every file
// they serve, public or CloudFront-signed: signed URLs keep their query params
// (`Policy`/`Signature`/`Key-Pair-Id`, or `Expires`) and the signature's wildcard
// accepts the extra `width`/`format`. Every other remote URL — S3 presigned, the
// public video distribution, external CDNs — must be used verbatim: an extra
// query param breaks a signature and the request 403s.
export const IMAGE_CDN_HOSTS = [
  "montu-media.montu.io",
  "staging-media.montu.io",
];

// Query params that change on every re-sign of the same file.
const SIGNING_PARAMS = ["Policy", "Signature", "Key-Pair-Id", "Expires"];

// A fixed set of widths keeps the number of variants the Lambda renders, and the
// CDN caches, small.
export const WIDTH_LADDER = [64, 128, 256, 384, 512, 768, 1024, 1536, 2048];

// `auto` lets the CDN pick AVIF/WebP from the request's `Accept` header. Native
// image loaders may not advertise either, so native asks for WebP explicitly.
export type ImageFormat = "auto" | "webp";

export const isResizableImageUri = (uri: string): boolean => {
  // Relative paths, `blob:`/`data:`/`file:` uris and garbage are never resized.
  try {
    return IMAGE_CDN_HOSTS.includes(new URL(uri).hostname);
  } catch {
    return false;
  }
};

// Rounds a physical pixel width up to the next ladder step, capped at the last one.
export const snapToWidthLadder = (pixels: number): number =>
  WIDTH_LADDER.find((width) => width >= pixels) ??
  WIDTH_LADDER[WIDTH_LADDER.length - 1];

// The raw query pairs minus the named params. Pairs are never decoded and
// re-encoded: `URLSearchParams` would rewrite `~` (CloudFront-safe base64) as
// `%7E`, and a signed URL must reach the CDN byte-for-byte.
const rawQueryWithout = (search: string, names: string[]): string[] =>
  search
    .replace(/^\?/, "")
    .split("&")
    .filter((pair) => pair !== "" && !names.includes(pair.split("=")[0]));

export const buildResizedImageUri = (
  uri: string,
  width: number,
  format: ImageFormat,
): string => {
  const url = new URL(uri);
  const pairs = rawQueryWithout(url.search, ["width", "format"]);
  url.search = [...pairs, `width=${width}`, `format=${format}`].join("&");
  return url.toString();
};

// The uri without the params that rotate on every re-sign, so the same image
// keeps one image-cache entry across days. Path, `width` and `format` stay: they
// select the variant. Only for the media CDN, whose signing params we know:
// `undefined` for any other host or anything that is not a remote http(s) uri.
export const stableImageCacheKey = (uri: string): string | undefined => {
  if (!isResizableImageUri(uri)) return undefined;
  try {
    const url = new URL(uri);
    if (url.protocol !== "http:" && url.protocol !== "https:") return undefined;
    url.search = rawQueryWithout(url.search, SIGNING_PARAMS).join("&");
    url.hash = "";
    return url.toString();
  } catch {
    return undefined;
  }
};
